import 'dayjs/locale/de';
import 'dayjs/locale/nl';
// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// eslint-disable-next-line no-restricted-imports
export { Dayjs } from 'dayjs';

dayjs.locale('de');

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const DEFAULT_TIMEZONE = 'Europe/Berlin';
dayjs.tz.setDefault(process.env.TZ ?? DEFAULT_TIMEZONE);

/**
 * This patched version of dayjs will format times in Europe/Berlin timezone.
 *
 * #Examples
 *
 * ##Formatting
 * ```
 * import dayjs from '@shared/dayjs';
 * dayjs(<input>).format();
 * ```
 *
 * ##Formatting in a different timezone
 * ```
 * import dayjs from '@shared/dayjs';
 * dayjs(<input>).tz('America/New_York').format();
 * ```
 *
 * ##Parsing in Berlin-local time
 * `dayjs.tz(<input>, 'Europe/Berlin')`
 *
 * ##Parsing properly formatted input with timestamp
 * `dayjs(<input>)`
 */
const dayjsWithDefaultTimezone = ((
  ...args: Parameters<typeof dayjs>
): ReturnType<typeof dayjs> => {
  return dayjs(...args).tz(DEFAULT_TIMEZONE);
}) as typeof dayjs;
Object.assign(dayjsWithDefaultTimezone, dayjs);

export default dayjsWithDefaultTimezone;

export const DEFAULT_ENGLISH_DATE_FORMAT = 'DD/MM/YYYY'; // 01/01/2001
export const DEFAULT_GERMAN_DATE_FORMAT = 'DD.MM.YYYY'; // 01.01.2001
export const DEFAULT_GERMAN_TIME_FORMAT = 'HH:mm:ss'; // 12:34:56
export const DEFAULT_GERMAN_DATE_TIME_FORMAT = `${DEFAULT_GERMAN_DATE_FORMAT} ${DEFAULT_GERMAN_TIME_FORMAT}`; // 01.01.2001 12:34:56
export const DEFAULT_GERMAN_DATE_TIME_FORMAT_SHORT = `DD.MM. HH:mm`; // 01.01. 12:34
const GERMAN_DATE_FORMATS = [
  'D.M.YYYY', // 1.1.2001
  DEFAULT_GERMAN_DATE_FORMAT,
  'D.MM.YYYY', // 1.01.2001 (inconsistent but still considered valid)
  'DD.M.YYYY', // 01.1.2001 (inconsistent but still considered valid)
];
export const parseGermanDate = (input: string): ReturnType<typeof dayjs> => {
  return dayjs(input, GERMAN_DATE_FORMATS, true).tz(DEFAULT_TIMEZONE, true);
};
